// src/config/firebase.ts
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getPerformance } from 'firebase/performance';
// SECURITY: All Firebase credentials must be provided via environment variables
// Never commit hardcoded credentials to version control
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET'
];

const missingEnvVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);
if (missingEnvVars.length > 0) {
  throw new Error(`Missing required environment variables: ${missingEnvVars.join(', ')}`);
}

// Debug logging for development
if (import.meta.env.DEV) {
  console.log('🔥 Firebase Config Debug:', {
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.slice(0, 10)}...` : 'missing',
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    hasEnvVars: {
      apiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: !!import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: !!import.meta.env.VITE_FIREBASE_PROJECT_ID
    }
  });
}

// Initialize Firebase with error handling
let app;
let analytics;
let performance;
export let auth;
export let db;
export let storage;
export let messaging;

try {
  app = initializeApp(firebaseConfig);
  
  // Initialize Firebase services
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  
  // Initialize messaging for push notifications
  isSupported().then((supported) => {
    if (supported) {
      try {
        messaging = getMessaging(app);
        console.log('🔥 Firebase Messaging initialized successfully');
      } catch (messagingError) {
        console.warn('🔥 Firebase Messaging initialization failed:', messagingError);
        messaging = null;
      }
    } else {
      console.warn('🔥 Firebase Messaging not supported in this environment');
      messaging = null;
    }
  }).catch((error) => {
    console.warn('🔥 Error checking messaging support:', error);
    messaging = null;
  });
  
  // Only initialize analytics if not in development or if explicitly enabled
  if (!import.meta.env.DEV || import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
    try {
      analytics = getAnalytics(app);
      console.log('📊 Firebase Analytics initialized successfully');
    } catch (analyticsError) {
      console.warn('📊 Analytics initialization failed:', analyticsError);
    }
  }
  
  // Initialize Performance Monitoring in production
  if (!import.meta.env.DEV) {
    try {
      performance = getPerformance(app);
      console.log('⚡ Firebase Performance initialized successfully');
    } catch (performanceError) {
      console.warn('⚡ Performance monitoring initialization failed:', performanceError);
    }
  }
  
  console.log('🔥 Firebase initialized successfully');
} catch (error) {
  console.error('🔥 Firebase initialization failed:', error);
  
  // Provide fallback objects to prevent app crashes
  auth = null;
  db = null;
  storage = null;
}

// Export analytics and performance for use in other modules
export { analytics, performance };

export default app;